// BUILD-042 October 8 21:19 addendum root-cause fix — "clear identity/perspective."
//
// WHY THIS EXISTS: the Owner's failed retest showed "My HR / Local Reviewer" instead of the
// expected Larry-as-HR-Administrator view. Root cause (confirmed by inspecting the walkthrough
// backend's actual persisted state): the prior pass's browser-automation script called
// POST /local/switch-caller to impersonate Larry for its own self-service test step, and never
// switched back to the Owner afterward. The backend then silently kept treating every
// subsequent request as "Larry," while this frontend's OWN separate local-mock-auth layer
// (AuthContext.tsx) always displays the fixed label "Local Reviewer" regardless of which
// employeeId the backend actually resolves — two independent identity notions, with NO visible
// indicator anywhere that they could differ. That silent mismatch, not a code defect in the
// document screens themselves, is what Owner's screenshots actually showed.
//
// THE FIX: a small, clearly-labeled, LOCAL-ONLY control (same VITE_LOCAL_MOCK_AUTH guard as
// AuthContext.tsx's own mock, so it is statically dead-code-eliminated from any real build) that
// (a) displays which employeeId the LOCAL WALKTHROUGH BACKEND currently resolves as the caller,
// by name, and (b) lets Chris change it directly from the header, with an explicit confirmation,
// instead of a hidden curl/script call he cannot see or verify. This is purely a local test
// harness aid — it does not call any real HR route, does not touch Cognito, and has no effect
// whatsoever outside this disposable local walkthrough setup.
import { useEffect, useState } from 'react';
import { config } from '../../config';
import { useHrAccess } from './HrAccessContext';

const LOCAL_MOCK_AUTH = import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK_AUTH === 'true';

interface LocalPerson {
  employeeId: string;
  name: string;
  isFixture: boolean;
}

export function LocalCallerIndicator() {
  // Hard-guarded exactly like AuthContext.tsx's own LOCAL_MOCK_AUTH — statically false and
  // dead-code-eliminated in any real `vite build`, never active against the real deployed API.
  if (!LOCAL_MOCK_AUTH) return null;
  return <LocalCallerIndicatorInner />;
}

function LocalCallerIndicatorInner() {
  const [currentCallerEmployeeId, setCurrentCallerEmployeeId] = useState<string | null>(null);
  const [people, setPeople] = useState<LocalPerson[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // 2026-10-13 UAT: when the local backend caller changes, the HR sidebar surfaces
  // (My HR / My Team / HR Administration) must be re-resolved for the NEW identity so the left
  // nav matches who you are now viewing as — e.g. an employee identity shows no "HR
  // Administration" item, an admin identity does. HrAccessContext otherwise resolves once at
  // load. Local-walkthrough only (this whole component is behind VITE_LOCAL_MOCK_AUTH).
  const { reload: reloadHrAccess } = useHrAccess();

  async function refresh() {
    try {
      const res = await fetch(`${config.apiUrl}/local/switch-caller`);
      if (!res.ok) throw new Error(`local backend returned ${res.status}`);
      const data = await res.json();
      setCurrentCallerEmployeeId(data.currentCallerEmployeeId ?? null);
      setPeople(data.people ?? []);
      setError(null);
    } catch (e: any) {
      // Not every VITE_LOCAL_MOCK_AUTH session targets the disposable walkthrough backend (the
      // SAME flag is also used for the older, unrelated local-mock-admin-people-access.mjs /
      // local-mock-hr-review.mjs setups), so a failed probe here is expected and must stay
      // silent-but-visible rather than throwing — this control simply has nothing to show.
      setError('No local walkthrough backend detected at this API URL.');
    }
  }

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, []);

  async function handleSwitch(employeeId: string) {
    if (!employeeId || employeeId === currentCallerEmployeeId || pending) return;
    setPending(true);
    try {
      const res = await fetch(`${config.apiUrl}/local/switch-caller`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId }),
      });
      if (!res.ok) throw new Error(`switch failed: ${res.status}`);
      await refresh();
      // Re-resolve HR surfaces for the newly-selected identity so the left HR nav immediately
      // reflects it (employee identity -> no HR Administration item; admin identity -> shows it).
      reloadHrAccess();
    } catch (e: any) {
      setError(e.message || 'Failed to switch the local caller.');
    } finally {
      setPending(false);
    }
  }

  if (error) {
    return (
      <div className="hidden sm:block text-[10px] text-amber-400 max-w-[220px] text-right leading-tight" title={error}>
        Local backend caller: unavailable
      </div>
    );
  }

  const currentPerson = people.find((p) => p.employeeId === currentCallerEmployeeId);

  return (
    <div className="hidden sm:flex flex-col items-end gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-gray-400">Local backend caller:</span>
        <select
          aria-label="Switch local walkthrough caller"
          value={currentCallerEmployeeId ?? ''}
          disabled={pending}
          onChange={(e) => handleSwitch(e.target.value)}
          className="text-[11px] bg-amber-500/20 border border-amber-400/40 text-amber-200 rounded px-1.5 py-0.5 disabled:opacity-50"
        >
          {people.map((p) => (
            <option key={p.employeeId} value={p.employeeId}>
              {p.name}{p.isFixture ? ' (fictional)' : ''}
            </option>
          ))}
        </select>
      </div>
      <span className="text-[10px] text-amber-400/80 leading-tight">
        Not Cognito — this is who the LOCAL backend treats as logged in{currentPerson ? `: ${currentPerson.name}` : ''}
      </span>
    </div>
  );
}
